import { DataTableViewOptions } from "@/components/ColumnToggle";
import Container from "@/components/Container";
import Header from "@/components/Header";
import { DataTablePagination } from "@/components/Pagination";
import { useCollectors } from "@/hooks/collector/useCollector";
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useCallback, useState } from "react";
import { DateRange } from "react-day-picker";
import AddCollection from "./addCollectio/AddCollection";
import Call from "./collectorList/Call";
import Collect from "./collectorList/Collect";
import CollectorViewMobile from "./collectorList/CollectorViewMobile";
import { columns } from "./collectorList/columns";
import { DataTable } from "./collectorList/data-table";
import Deposit from "./collectorList/Deposit";
import { getStatusColor } from "./collectorList/filters/StatusDropdown";

type PropsCollector = {};

const Collector: React.FC<PropsCollector> = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    selectedWilaya: "",
    status: "",
    selectedCollector: "",
    dateRange: undefined as DateRange | undefined,
  });

  const { collectors, refresh, meta, loading, error } = useCollectors(
    pageIndex + 1,
    pageSize,
    searchTerm,
    filters.selectedWilaya,
    filters.status,
    filters.selectedCollector,
    filters.dateRange
  );

  const handleRowSelect = (row: any, isSelected: boolean) => {
    setSelectedRows((prev) =>
      isSelected
        ? [...prev, row.original]
        : prev.filter((item) => item.id !== row.original.id)
    );
  };

  const table = useReactTable({
    data: collectors || [],
    columns: [
      {
        id: "select",
        header: "",
        cell: ({ row }) => (
          <>
            {row.original.status === "Payment Collected" ? (
              <input
                type="checkbox"
                checked={selectedRows.some(
                  (item) => item.id === row.original.id
                )}
                onChange={(e) => handleRowSelect(row, e.target.checked)}
              />
            ) : null}
          </>
        ),
      },
      ...columns,
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }: any) => (
          <p
            className="font-medium"
            style={{ color: getStatusColor(row.getValue("status")) }}
          >
            {row.getValue("status")}
          </p>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Call customer={row.original.Customer} />
            <Collect
              collection={row.original as any}
              onCollect={handleRefresh}
            />
          </div>
        ),
      },
    ],
    pageCount: meta ? meta.lastPage : -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    table.setPageSize(newPageSize);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
    table.setPageIndex(newPageIndex);
  };

  // const handleFiltersChange = (newFilters: {
  //   selectedWilaya: string;
  //   status: string;
  //   selectedCollector: string;
  //   dateRange: DateRange | undefined;
  // }) => {
  //   setFilters(newFilters);
  // };

  const [depositRefreshKey, setDepositRefreshKey] = useState(0);
  const [collectRefreshKey, setCollectRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => {
    console.log("trigger refresh in collector");
    refresh(); // Trigger refresh in the Collector component
    setDepositRefreshKey((prevKey) => prevKey + 1); // Update state to trigger DepositMobile refresh
    setCollectRefreshKey((prevKey) => prevKey + 1); // Update state to trigger CollectMobile refresh
  }, [refresh, setDepositRefreshKey, setCollectRefreshKey]);

  return (
    <Container isSmallScreenReady>
      <Header placeholder="Search Collector" setSearchTerm={setSearchTerm} />
      <div className="flex-col hidden h-full gap-2 p-4 md:flex">
        <div className="flex items-center justify-between">
          <h1 className="font-semibold text-[18px]">Collector List</h1>
          <div className="flex items-center gap-4">
            <AddCollection onAddCollectionOrder={refresh} />
            <Deposit onDeposit={handleRefresh} selectedRows={selectedRows} />
          </div>
        </div>

        <div className="flex-grow py-2 overflow-y-scroll">
          <DataTable
            table={table}
            columns={columns}
            loading={loading}
            error={error}
          />
        </div>
        <div className="flex justify-between pt-2 mt-auto">
          <div>
            <DataTableViewOptions table={table} />
          </div>
          <div className="flex justify-end w-full ">
            <DataTablePagination
              table={table}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>
      {/* Pass handleRefresh to CollectorViewMobile to allow it to trigger a refresh in Collector */}
      <CollectorViewMobile
        setDepositRefreshKey={setDepositRefreshKey}
        depositRefreshKey={depositRefreshKey}
        setCollectRefreshKey={setCollectRefreshKey}
        collectRefreshKey={collectRefreshKey}
        refresh={handleRefresh}
      />
    </Container>
  );
};

export default Collector;
